import { accountApi, apiFetch, type AccountApi, type ApiTarget } from "./account-api.js";
import { PublicationCoordinator, type Publication } from "./publication-coordinator.js";
import { requestComposer } from "./request-composer.js";
import { withDeadline } from "./deadline.js";
import { accountExtensions } from "./account-extensions.js";
import { CLIENT_HEADERS } from "./generated/compatibility.js";

import "./styles.css";

import { activityFeed, freshnessLabel, syncStatus, vaultPulse } from "./activity.js";
import {
  enqueueIntent,
  intentForAlert,
  markIntentFailed,
  markIntentSent,
  pendingIntents,
  readIntents,
  type AlertAction,
  type AlertIntent,
} from "./alert-intents.js";
import { activeArtifacts, createIsolatedArtifact } from "./artifact.js";
import { askPrompt, AUTHORSHIP_LINE, searchMiss, suggestedPath, type AskKind } from "./ask-ai.js";
import {
  countsLabel,
  derivedCounts,
  parseServerCounts,
  reconcileCounts,
  type CountsSnapshot,
} from "./counts.js";
import { installUnhandledReporter, showFatalState, workerStopped } from "./fatal-state.js";
import { mcpActivityLabel, parseMcpActivity, type McpActivity } from "./mcp-activity.js";
import { applyMobileWorkspace } from "./mobile-workspace.js";
import {
  alertDestination,
  DEFAULT_DESTINATION,
  destinationTitle,
  formatDestination,
  parseDestination,
  sameDestination,
  showsDomainView,
  webHref,
  type Destination,
  type SearchMode,
  type VaultFilter,
} from "./destinations.js";
import {
  fetchAccountExtensions,
  setAccountExtension,
  fetchAlerts,
  fetchArtifact,
  fetchConversation,
  fetchCounts,
  fetchJob,
  answerLedgerRequest,
  fetchLedgerNotification,
  fetchLedgerNotifications,
  fetchLedgerRequest,
  fetchLedgerRequests,
  fetchLedgerThread,
  fetchRequest,
  fetchResult,
  fetchRun,
  fetchSources,
  postAlertAction,
  type AlertRecord,
  type Fetched, type JobCheckin, type LedgerRequest, fetchPushPublicKey, registerWebPush } from "./platform-client.js";
import { answerAttempt, type AnswerDraft } from "./request-answer.js";
import { ledgerRequestMeta, ledgerRequestView, noticeMeta, noticeView, questionInboxView, type SubjectLink, type ThreadEntry } from "./request-detail.js";
import { INBOX_DIRECTION, INBOX_STATES, type OutstandingCounts } from "./question-inbox.js";
import { jobPhase, outboxPhase, requestPhase, runPhase } from "./run-state.js";
import {
  exchangeProviderAuthorization,
  exchangeProviderToken,
  linkProviderToken,
  readBrowserSession,
  requestProviderChallenge,
  signOutBrowserSession,
  type BrowserSession,
} from "./provider-auth.js";
import { AuthError, type AuthFailureCode } from "./auth-error.js";
import type { CapabilityReport, InitResult, NoteVersion, SyncReceipt, VaultNote } from "./types.js";
import { singleFlight } from "./single-flight.js";
import { VaultWorkerClient } from "./worker-client.js";
import { headerMetadata, noteTitle, readNote } from "./frontmatter.js";
import { enableWebPush, pushStatusLabel, refreshWebPush, type PushOutcome, type WebPushDeps } from "./web-push.js";
import { ancestorFolders, createFolderIndex, renderTree, rowKey, treeKeyIntent, treeRows, type NoteOrder, type TreeRow } from "./folder-tree.js";
import { SHELL_HTML } from "./shell.js";
import { alertContentCard, alertRow } from "./alert-rows.js";
import { connectionLabelWithDurability, ensureDurableStorage } from "./storage-durability.js";
import { connectionOverride, restoreOnStartup, startupWelcome, type StartupPhase } from "./startup-restore.js";
import {
  RAILS_SHOWING,
  readDismissedRelease,
  readExpandedFolders,
  readRailState,
  writeDismissedRelease,
  writeExpandedFolders,
  writeRailState,
  type RailState,
} from "./view-state.js";
import { RELEASE, releaseLabel } from "./release.js";
import { discardShell, updatePrompt, watchForUpdate, type UpdateSignal } from "./service-worker-update.js";
import { backlinks, escapeHtml, makeEtag, makeId, renderMarkdown, vaultSelection } from "./vault.js";
import { cachedOfflineAccount, forgetLocalAccount, forgetSessionValidation, rememberValidatedSession } from "./session-cache.js";

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || "https://mcp.recallvault.dev";
let appleClientId = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined;
let googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const authorizationParams = new URLSearchParams(location.search);
const mcpAuthorization = authorizationParams.get("mcp_authorization");
const mcpAuthorizationNonce = authorizationParams.get("nonce");
const isMcpAuthorization = mcpAuthorization !== null && mcpAuthorizationNonce !== null
  && /^[A-Za-z0-9_-]{43}$/.test(mcpAuthorization)
  && /^[A-Za-z0-9_-]{43}$/.test(mcpAuthorizationNonce);

interface GoogleIdentityApi {
  accounts: { id: {
    initialize(config: { client_id: string; nonce: string; callback(response: { credential?: string }): void }): void;
    renderButton(element: HTMLElement, config: Record<string, string | number | boolean>): void;
  } };
}

interface AppleIdentityApi {
  auth: {
    init(config: { clientId: string; scope: string; redirectURI: string; usePopup: boolean; nonce: string }): void;
    signIn(): Promise<{ authorization?: { id_token?: string } }>;
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityApi;
    AppleID?: AppleIdentityApi;
  }
}
/**
 * The element the whole client is mounted in.
 *
 * Resolved through a function so the type is non-nullable everywhere, including
 * inside hoisted declarations like `reportFailure` — which needs it precisely
 * when things have gone wrong.
 */
function appRoot(): HTMLDivElement {
  const element = document.querySelector<HTMLDivElement>("#app");
  if (!element) throw new Error("Missing app root");
  return element;
}

const root = appRoot();


root.innerHTML = SHELL_HTML;

const select = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing element: ${selector}`);
  return element;
};

const noteList = select<HTMLDivElement>("[data-note-list]");
const preview = select<HTMLElement>("[data-preview]");
const sourceView = select<HTMLPreElement>("[data-source]");
const searchDialog = select<HTMLDialogElement>("[data-search-dialog]");
const searchInput = select<HTMLInputElement>("[data-search-input]");
const searchResults = select<HTMLDivElement>("[data-search-results]");
const accountDialog = select<HTMLDialogElement>("[data-account-dialog]");
const historyDialog = select<HTMLDialogElement>("[data-history-dialog]");
const askDialog = select<HTMLDialogElement>("[data-ask-dialog]");
const askPromptField = select<HTMLTextAreaElement>("[data-ask-prompt]");
const toast = select<HTMLDivElement>("[data-toast]");
const updateBar = select<HTMLDivElement>("[data-update-bar]");
// Stamped once at start-up: a shell that has gone stale can still say which
// build it is, which is the difference between "broken" and "merely old".
select("[data-release]").textContent = releaseLabel(RELEASE);
const authGate = select<HTMLElement>("[data-auth-gate]");
const appFrame = select<HTMLElement>("[data-app-frame]");
const authStatus = select<HTMLElement>("[data-auth-status]");

let client = new VaultWorkerClient();
let accountId = "";
const publication = new PublicationCoordinator();
function hostedApi(snapshot = publication.capture()): AccountApi {
  const bound = accountApi(API_ORIGIN, snapshot.accountId.replace(/^hosted:/, ""), fetch, () => publication.current(snapshot));
  return { ...bound, async request(path, init) {
    try { return await bound.request(path, init); }
    catch (error) {
      if (error instanceof AuthError && publication.current(snapshot)) run(handleAuthFailure(error.code));
      throw error;
    }
  } };
}
const extensionPanel = accountExtensions(select<HTMLElement>("[data-account-extensions]"), {
  list: () => fetchAccountExtensions(hostedApi()),
  set: (id, enabled, changeId) => setAccountExtension(hostedApi(), id, enabled, changeId),
  newId: () => crypto.randomUUID(),
});
let notes: VaultNote[] = [];
let receipts: SyncReceipt[] = [];
let capabilities: CapabilityReport | undefined;
let selectedId = "";
let showingSource = false;
let syncing = false;
/** Whether this launch has asked the browser for durable storage yet. */
let storageRequested = false;
/** Set when the vault opened from the cache because the server was unreachable. */
let offlineStart = false;
/** The start-up restore phase; ready once the local store is trusted. */
let startupState: StartupPhase = "ready";
/**
 * The address bar is the source of truth for where the user is. Every surface
 * derives from this one value, so a destination can be linked to, restored on
 * reload, and — crucially — targeted by an alert.
 */
let destination: Destination = DEFAULT_DESTINATION;
let filter: VaultFilter = "all";
let searchMode: SearchMode = "exact";
let counts: CountsSnapshot = reconcileCounts(derivedCounts([]), undefined);
/** Whether an AI has ever connected: `undefined` until the server says. */
let mcpActivity: McpActivity | null | undefined;
let intents: readonly AlertIntent[] = [];
let alerts: readonly AlertRecord[] = [];
let deferredInstall: Event | undefined;
let rails: RailState = RAILS_SHOWING;
/** Folder paths the reader has opened. UI state, never part of the vault. */
let expandedFolders = new Set<string>();
/** The tree row that owns the keyboard, so the tree is one tab stop, not 857. */
let treeFocus = "";
const folderIndex = createFolderIndex();
let searchTimer = 0;
let applePendingChallenge: string | undefined;
let applePendingPurpose: "session" | "link" = "session";
const composer = requestComposer(askDialog, () => hostedApi(), (id, quarantined) => {
  askDialog.close();
  if (quarantined) showToast("Attachment stored for download only; inline rendering is disabled.");
  navigate({ kind: "ledger-request", id });
});
const traceEvents: string[] = [];
const tabChannel = "BroadcastChannel" in window ? new BroadcastChannel("recallvault-vault-events") : undefined;

function browserStore(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function selected(): VaultNote | undefined {
  return notes.find(({ id }) => id === selectedId);
}

function trace(event: string): void {
  traceEvents.unshift(`${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · ${event}`);
  traceEvents.splice(5);
  select<HTMLOListElement>("[data-trace]").innerHTML = traceEvents.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
}

function showToast(message: string): void {
  toast.textContent = message;
  toast.classList.add("visible");
  window.setTimeout(() => toast.classList.remove("visible"), 2800);
}

/**
 * The one place a failure nobody expected becomes something the owner can see.
 *
 * A dead vault worker and a failed request are not the same event and must not
 * get the same treatment. The worker's latch is permanent — every later call
 * rejects immediately — so it earns the terminal state, and it is asked about
 * first because a latched worker is *why* the rejection in hand happened. Any
 * other failure is transient as far as this client knows, and a toast is
 * honest about that where wiping the app would not be.
 */
function reportFailure(reason: unknown): void {
  const latched = client.failure;
  if (latched) {
    showFatalState(root, workerStopped(latched), () => location.reload());
    return;
  }
  showToast(reason instanceof Error ? reason.message : "Something went wrong. Try again.");
}

/**
 * Runs work started for its effect, so a rejection reaches `reportFailure`
 * rather than the console.
 *
 * Every one of these used to be a bare `void`. The global reporter would catch
 * them now anyway; keeping the `catch` here means a handler says what it does
 * with a failure at the point it starts the work.
 */
function run(work: Promise<unknown>): void {
  const snapshot = publication.capture();
  void work.catch((reason: unknown) => {
    if (snapshot.accountId && !publication.current(snapshot)) return;
    reportFailure(reason);
  });
}

// Hung before start-up so a failure during it is reported too. The start-up
// IIFE's own `catch` still covers the case where the shell never opened at all.
installUnhandledReporter(window, reportFailure);

function stateLabel(state: VaultNote["state"]): string {
  return ({ local: "Saved locally", queued: "Waiting to sync", synced: "Synced", conflict: "Needs review" })[state];
}

/**
 * The visible list, memoised on the note list it came from.
 *
 * The folder index keys off this array's identity. Handing it a freshly built
 * array on every render would rebuild the whole tree for a migration-sized
 * vault each time a selection or a badge changed.
 */
let filterCache: { readonly key: VaultFilter; readonly source: readonly VaultNote[]; readonly value: readonly VaultNote[] } | undefined;

function filteredNotes(): readonly VaultNote[] {
  if (filterCache?.key === filter && filterCache.source === notes) return filterCache.value;
  const value = filter === "recent" || filter === "today"
    ? notes.slice(0, 12)
    : filter === "pinned"
      // Pinning is device-local state, and this client keeps none. An empty
      // list is the truthful answer for a browser, not a bug — see the empty
      // state below, which says so rather than implying the vault is clear.
      ? []
      : filter === "queued"
        ? notes.filter(({ state }) => state === "queued")
        : filter === "conflict"
          ? notes.filter(({ state }) => state === "conflict")
          : notes;
  filterCache = { key: filter, source: notes, value };
  return value;
}

function folderContent(row: Extract<TreeRow, { kind: "folder" }>): string {
  return `<div class="folder-row" style="--depth:${row.depth}">
      <span class="twisty" aria-hidden="true">${row.expanded ? "▾" : "▸"}</span>
      <span class="folder-name">${escapeHtml(row.name)}</span>
      <span class="count">${row.total}</span>
    </div>`;
}

/** "Recently changed" orders the tree by recency; every other filter browses by title. */
function noteOrder(): NoteOrder {
  return filter === "recent" || filter === "today" ? "recency" : "title";
}

/** The list, indexed as a tree; memoised on the filtered list's identity. */
function currentTree() {
  return folderIndex(filter, filteredNotes(), noteOrder());
}

function noteContent({ note, depth, hint }: Extract<TreeRow, { kind: "note" }>): string {
  const when = new Date(note.updatedAt).toLocaleDateString([], { month: "short", day: "numeric" });
  const detail = hint === undefined ? when : `${when} · <span class="note-hint">${escapeHtml(hint)}</span>`;
  return `<div class="note-row ${note.id === selectedId ? "selected" : ""}" style="--depth:${depth}">
      <span class="note-glyph" aria-hidden="true">${note.state === "conflict" ? "◇" : "≡"}</span>
      <span><strong>${escapeHtml(note.title)}</strong><small>${detail}</small></span>
      <i class="state-dot ${note.state}" title="${stateLabel(note.state)}"></i>
    </div>`;
}

/**
 * Opens the folders a note sits in, so a note reached by link, search, or a
 * backlink is visible in the tree rather than hidden inside a closed area.
 */
function revealFolders(path: string): void {
  const before = expandedFolders.size;
  for (const folder of ancestorFolders(path)) expandedFolders.add(folder);
  if (expandedFolders.size !== before) writeExpandedFolders(browserStore(), accountId, expandedFolders);
}

/** Why this list is empty, which differs by filter and is never a shrug. */
function emptyListReason(): string {
  if (filter === "all") return "Ask your AI to write a note, then sync.";
  if (filter === "pinned") return "Pinning lives on each device. Pin notes in the app to see them here.";
  return "Your vault is clear.";
}

function renderNoteList(): void {
  const selection = selected();
  if (selection) revealFolders(selection.path);
  const tree = currentTree();
  const rows = treeRows(tree, expandedFolders);
  if (!rows.some((row) => rowKey(row) === treeFocus)) treeFocus = rows[0] ? rowKey(rows[0]) : "";
  noteList.innerHTML = rows.length === 0
    ? `<div class="mini-empty"><strong>Nothing here</strong><span>${emptyListReason()}</span></div>`
    : renderTree(tree, expandedFolders, { focusKey: treeFocus, selectedId, folderContent, noteContent });
}

function focusTreeRow(key: string): void {
  treeFocus = key;
  noteList.querySelectorAll<HTMLElement>("[data-row]").forEach((row) => {
    row.tabIndex = row.dataset.row === key ? 0 : -1;
  });
  noteList.querySelector<HTMLElement>(`[data-row="${CSS.escape(key)}"]`)?.focus();
}

function toggleFolder(path: string, open = !expandedFolders.has(path)): void {
  if (open) expandedFolders.add(path);
  else expandedFolders.delete(path);
  writeExpandedFolders(browserStore(), accountId, expandedFolders);
  renderNoteList();
  focusTreeRow(`folder:${path}`);
}

function renderReceipts(): void {
  const container = select<HTMLDivElement>("[data-receipts]");
  container.innerHTML = receipts.length === 0 ? `<p class="muted">No sync attempts yet.</p>` : receipts.slice(0, 3).map((receipt) => `
    <div class="receipt ${receipt.outcome}"><span>${receipt.outcome === "synced" ? "✓" : receipt.outcome === "failed" ? "!" : "·"}</span><div><strong>${escapeHtml(receipt.detail)}</strong><small>${new Date(receipt.at).toLocaleString()}</small></div></div>
  `).join("");
}

function renderActivity(): void {
  const entries = activityFeed(notes, receipts, 8);
  const container = select<HTMLDivElement>("[data-activity]");
  container.innerHTML = entries.length === 0
    ? `<p class="muted">Nothing has changed on this device yet. Sync to pull what your AI wrote.</p>`
    : entries.map((entry) => `
      <${entry.noteId ? `button class="activity-row ${entry.tone}" type="button" data-note="${escapeHtml(entry.noteId)}"` : `div class="activity-row ${entry.tone}"`}>
        <i aria-hidden="true">${entry.kind === "sync" ? "↻" : entry.tone === "attention" ? "◇" : "≡"}</i>
        <span><strong>${escapeHtml(entry.headline)}</strong><small>${escapeHtml(entry.detail)}</small><time>${new Date(entry.at).toLocaleString()}</time></span>
      </${entry.noteId ? "button" : "div"}>`).join("");
}

/* ------------------------------------------------------ domain surfaces */

function phasePill(headline: string, phase: string): string {
  return `<span class="phase-pill ${phase}">${escapeHtml(headline)}</span>`;
}

/**
 * What a destination shows while its server route does not exist. It names the
 * object, not a screen, and says plainly that the address is real and the data
 * is not — which is the truth, and more useful than an empty list.
 */
function unavailableCard(title: string, detail: string): string {
  return `<div class="domain-empty"><strong>${escapeHtml(title)}</strong><p>${escapeHtml(detail)}</p></div>`;
}

/** Turns a failed fetch into the card the user should see for that reason. */
function failureCard(title: string, result: Extract<Fetched<unknown>, { ok: false }>): string {
  return unavailableCard(title, result.detail);
}

let domainBody = "";
let domainMeta = "";

/**
 * The question currently on screen, with what the person has typed into it.
 *
 * Held here rather than in the DOM so a re-render never loses a half-typed
 * reply, and so the answering path has exactly one source of truth about which
 * question a submission belongs to. It is cleared whenever the page moves, so a
 * submission can never land on a question the person has navigated away from.
 */
interface OpenQuestion {
  readonly request: LedgerRequest;
  readonly draft: AnswerDraft;
  readonly submitting: boolean;
  readonly error: string | null;
  readonly thread: readonly ThreadEntry[];
  /** True when the thread read stopped at the page cap with more still behind it. */
  readonly threadTruncated: boolean;
}

let openQuestion: OpenQuestion | null = null;

/**
 * The outstanding-questions inbox, held across renders so "read more" appends
 * to what is already on screen instead of restarting the listing.
 */
interface QuestionInbox {
  readonly requests: readonly LedgerRequest[];
  readonly counts: OutstandingCounts;
  readonly nextCursor: string | null;
  readonly loadingMore: boolean;
}

let questionInbox: QuestionInbox | null = null;

/** Where a `subject_ref` opens: a note in this vault, a link, or neither. */
function subjectLink(subjectRef: string): SubjectLink {
  const note = notes.find((candidate) => candidate.path === subjectRef);
  if (note) return { kind: "destination", value: `vault/note/${note.id}` };
  if (/^https:\/\//.test(subjectRef)) return { kind: "external", value: subjectRef };
  return { kind: "plain", value: subjectRef };
}

/**
 * Reads the rest of a question's thread, when it has one.
 *
 * Both histories are queried because a thread is deliberately mixed: an
 * ask-back is a question, and the confirmation that follows it is a notice.
 * A history that cannot be read is not an error worth blocking the answer over,
 * so a failure here simply yields no earlier rows.
 */
async function threadOf(request: LedgerRequest, api: ApiTarget): Promise<{ entries: readonly ThreadEntry[]; truncated: boolean }> {
  const key = request.threadKey;
  if (key == null || key.length === 0) return { entries: [], truncated: false };
  const [questions, notices] = await Promise.all([
    fetchLedgerThread(api, key),
    fetchLedgerNotifications(api, key),
  ]);
  return {
    entries: [
      ...(questions.ok ? questions.value.records.map((record): ThreadEntry => ({ kind: "request", record })) : []),
      ...(notices.ok ? notices.value.records.map((record): ThreadEntry => ({ kind: "notification", record })) : []),
    ],
    // Either history stopping short leaves the assembled thread short, so
    // either one saying so makes the whole thread say so.
    truncated: (questions.ok && questions.value.truncated) || (notices.ok && notices.value.truncated),
  };
}

/** Draws the open question, reusing the thread already read for it. */
function renderQuestion(): void {
  if (openQuestion === null) return;
  domainMeta = ledgerRequestMeta(openQuestion.request);
  domainBody = ledgerRequestView({
    request: openQuestion.request,
    subject: subjectLink(openQuestion.request.subjectRef),
    draft: openQuestion.draft,
    submitting: openQuestion.submitting,
    error: openQuestion.error,
    thread: openQuestion.thread,
    threadTruncated: openQuestion.threadTruncated,
  });
  renderDomain();
}

async function refreshQuestionThread(snapshot: Publication, request: LedgerRequest, api: ApiTarget): Promise<void> {
  const thread = await threadOf(request, api);
  publication.publish(snapshot, () => {
    if (openQuestion === null) return;
    openQuestion = { ...openQuestion, thread: thread.entries, threadTruncated: thread.truncated };
    renderQuestion();
  });
}

async function showQuestion(snapshot: Publication, request: LedgerRequest,
  rest: { draft: AnswerDraft; submitting: boolean; error: string | null }, api: ApiTarget): Promise<void> {
  const carried = openQuestion?.request.requestId === request.requestId
    ? { thread: openQuestion.thread, threadTruncated: openQuestion.threadTruncated }
    : { thread: [] as readonly ThreadEntry[], threadTruncated: false };
  if (!publication.publish(snapshot, () => { openQuestion = { request, ...rest, ...carried }; renderQuestion(); })) return;
  await refreshQuestionThread(snapshot, request, api);
}

/**
 * Sends the owner's decision, and shows exactly what the ledger did with it.
 *
 * The refusal path is the load-bearing one. A rejected or conflicting answer
 * re-reads the question and redraws it in the state the server actually holds,
 * so a question that was answered on the phone, replaced by a newer version, or
 * withdrawn stops offering controls here instead of silently accepting a second
 * decision. Nothing in this function writes an answer the person did not make.
 */
async function submitAnswer(): Promise<void> {
  const current = openQuestion;
  if (current === null || current.submitting) return;
  const attempt = answerAttempt(current.request, current.draft);
  if (!attempt.ok) {
    openQuestion = { ...current, error: attempt.detail };
    renderQuestion();
    return;
  }
  publication.navigate();
  const snapshot = publication.capture(true);
  const api = hostedApi(snapshot);
  openQuestion = { ...current, submitting: true, error: null };
  renderQuestion();
  const result = await answerLedgerRequest(api, current.request.requestId, attempt);
  if (result.ok) {
    await showQuestion(snapshot, result.value.request, { draft: {}, submitting: false, error: null }, api);
    publication.publish(snapshot, () => showToast("Answer recorded."));
    return;
  }
  if (result.reason === "conflict") {
    const reread = await fetchLedgerRequest(api, current.request.requestId);
    if (reread.ok) {
      await showQuestion(snapshot, reread.value.request, { draft: current.draft, submitting: false, error: result.detail }, api);
      return;
    }
  }
  publication.publish(snapshot, () => { openQuestion = { ...current, submitting: false, error: result.detail }; renderQuestion(); });
}

/** The last check-in as facts: counts, the two timestamps, and when the job was last heard from. */
function jobCheckinCard(job: JobCheckin): string {
  const counts = job.counts;
  const countRows = counts === null
    ? `<p>No counts reported yet.</p>`
    : `<p>Listed ${counts.listed} · resolved ${counts.resolved} · raised ${counts.raised} · failed ${counts.failed}</p>`;
  const when = (value: string | null): string => (value ? new Date(value).toLocaleString() : "never");
  return `<div class="domain-empty"><strong>Last check-in</strong>${countRows}`
    + `<p>Started ${escapeHtml(when(job.lastStartedAt))} · finished ${escapeHtml(when(job.lastFinishedAt))}</p>`
    + `<p>Last seen ${escapeHtml(when(job.lastSeenAt))}</p></div>`;
}

/** Draws the inbox from whatever has been read so far. */
function renderQuestionInbox(): void {
  if (questionInbox === null) return;
  domainMeta = "Questions the assistant raised to you. Answering one here is the same decision as answering its alert.";
  domainBody = questionInboxView({
    requests: questionInbox.requests,
    counts: questionInbox.counts,
    hasMore: questionInbox.nextCursor != null,
    loadingMore: questionInbox.loadingMore,
  });
  renderDomain();
}

/**
 * Reads the next page of the inbox and appends it.
 *
 * Explicit rather than automatic: an inbox is a queue to work through, and
 * reading one page at a time on request is honest as long as the page says
 * more exist — which `questionInboxView` does.
 */
async function readMoreQuestions(): Promise<void> {
  const snapshot = publication.capture(true);
  const api = hostedApi(snapshot);
  const current = questionInbox;
  if (current === null || current.loadingMore || current.nextCursor == null) return;
  questionInbox = { ...current, loadingMore: true };
  renderQuestionInbox();
  const result = await fetchLedgerRequests(api, {
    direction: INBOX_DIRECTION, states: INBOX_STATES, cursor: current.nextCursor,
  });
  publication.publish(snapshot, () => {
    if (!result.ok) {
      questionInbox = { ...current, loadingMore: false };
      renderQuestionInbox();
      showToast(result.detail);
      return;
    }
    questionInbox = {
      requests: [...current.requests, ...result.value.requests],
      counts: { pending: result.value.pending, answeredNotConsumed: result.value.answeredNotConsumed },
      nextCursor: result.value.nextCursor,
      loadingMore: false,
    };
    renderQuestionInbox();
  });
}

interface DomainResult {
  readonly body: string;
  readonly meta: string;
  readonly alerts: readonly AlertRecord[];
  readonly question: OpenQuestion | null;
  readonly inbox: QuestionInbox | null;
  readonly pendingQuestions?: number;
}

/** Loads data and render state without changing any shared state. */
async function readDomainDestination(target: Destination, api: ApiTarget, heldAlerts: readonly AlertRecord[]): Promise<DomainResult> {
  let domainBody = "";
  let domainMeta = "";
  let alerts = heldAlerts;
  let openQuestion: OpenQuestion | null = null;
  let questionInbox: QuestionInbox | null = null;
  let pendingQuestions: number | undefined;
  const title = destinationTitle(target);
  switch (target.kind) {
    case "alerts":
    case "alert-history": {
      const scope = target.kind === "alerts" ? "live" : "history";
      const result = await fetchAlerts(api, scope);
      if (!result.ok) {
        alerts = [];
        domainBody = failureCard(title, result);
        break;
      }
      alerts = result.value.alerts;
      domainMeta = scope === "live"
        ? "Every alert opens the object that explains it. Acting on one is a decision about that object, queued here until the hosted vault accepts it."
        : "Alerts that have been resolved, dismissed, or expired.";
      domainBody = alerts.length === 0
        ? unavailableCard("Nothing needs you", "No alerts in this list.")
        : alerts.map((alert) => alertRow(alert, intents)).join("");
      break;
    }
    case "questions": {
      const result = await fetchLedgerRequests(api, { direction: INBOX_DIRECTION, states: INBOX_STATES });
      if (!result.ok) { questionInbox = null; domainBody = failureCard(title, result); break; }
      questionInbox = {
        requests: result.value.requests,
        counts: { pending: result.value.pending, answeredNotConsumed: result.value.answeredNotConsumed },
        nextCursor: result.value.nextCursor,
        loadingMore: false,
      };
      pendingQuestions = result.value.pending;
      break;
    }
    case "request": {
      const result = await fetchRequest(api, target.id);
      if (!result.ok) { domainBody = failureCard(title, result); break; }
      const { request } = result.value;
      const copy = requestPhase(request.state);
      domainMeta = `${request.type} · ${target.id}`;
      domainBody = `<div class="domain-state">${phasePill(copy.headline, copy.phase)}<p>${escapeHtml(copy.detail)}</p></div>`
        + (request.conversationId
          ? `<button class="quiet-button" type="button" data-go="conversation/${escapeHtml(request.conversationId)}">Open the conversation this came from</button>`
          : "");
      break;
    }
    case "ledger-request": {
      const result = await fetchLedgerRequest(api, target.id);
      if (!result.ok) { openQuestion = null; domainBody = failureCard(title, result); break; }
      openQuestion = { request: result.value.request, draft: {}, submitting: false, error: null, thread: [], threadTruncated: false };
      break;
    }
    case "notice": {
      const result = await fetchLedgerNotification(api, target.id);
      if (!result.ok) { domainBody = failureCard(title, result); break; }
      const { notification } = result.value;
      domainMeta = noticeMeta(notification);
      domainBody = noticeView(notification, subjectLink(notification.subjectRef));
      break;
    }
    case "run": {
      const result = await fetchRun(api, target.id);
      if (!result.ok) { domainBody = failureCard(title, result); break; }
      const { run } = result.value;
      const copy = runPhase(run.state, run.waitReason, run.failureDetail);
      domainMeta = `attempt ${run.attempt} · ${target.id}`;
      domainBody = `<div class="domain-state">${phasePill(copy.headline, copy.phase)}<p>${escapeHtml(copy.detail)}</p></div>`
        + `<button class="quiet-button" type="button" data-go="request/${escapeHtml(run.requestId)}">Open the request that asked for this</button>`;
      break;
    }
    case "conversation": {
      const result = await fetchConversation(api, target.id);
      domainBody = result.ok ? unavailableCard(title, "This conversation exists but has no reader yet.") : failureCard(title, result);
      break;
    }
    case "result": {
      const result = await fetchResult(api, target.id);
      domainBody = result.ok ? unavailableCard(title, "This result exists but has no reader yet.") : failureCard(title, result);
      break;
    }
    case "artifact": {
      const result = await fetchArtifact(api, target.id);
      domainBody = result.ok ? unavailableCard(title, "This artifact exists but has no viewer yet.") : failureCard(title, result);
      break;
    }
    case "job": {
      const result = await fetchJob(api, target.id);
      if (!result.ok) { domainBody = failureCard(title, result); break; }
      const { job } = result.value;
      const copy = jobPhase(job.verdict);
      domainMeta = `${job.job} · every ${Math.round(job.cadenceSeconds / 60)} min · ${job.runs} runs`;
      domainBody = `<div class="domain-state">${phasePill(copy.headline, copy.phase)}<p>${escapeHtml(copy.detail)}</p></div>`
        + jobCheckinCard(job);
      break;
    }
    case "sources":
    case "source": {
      const result = await fetchSources(api);
      domainBody = result.ok ? unavailableCard(title, "Integration health has no reader yet.") : failureCard(title, result);
      break;
    }
    case "alert": {
      domainMeta = target.id;
      // A notice published by the ledger has no object of its own to open, so
      // the alert *is* the thing to read. Its payload already carries the whole
      // notice, which is why a confirmation or an orphan summary is readable
      // here and can simply be dismissed rather than answered.
      // A link opened straight into an alert has no inbox behind it yet, so the
      // live list is read before deciding there is nothing to show.
      if (alerts.length === 0) {
        const live = await fetchAlerts(api, "live");
        if (live.ok) alerts = live.value.alerts;
      }
      const held = alerts.find((alert) => alert.alertId === target.id);
      domainBody = held === undefined
        ? unavailableCard(
          "This alert has no object to open",
          "Its subject was not one this client recognises, so there is nothing better to show than the alert itself.",
        )
        : alertContentCard(held);
      break;
    }
    default:
      domainBody = unavailableCard(title, "");
      break;
  }
  return { body: domainBody, meta: domainMeta, alerts, question: openQuestion, inbox: questionInbox, pendingQuestions };
}


function commitDomain(snapshot: Publication, result: DomainResult): boolean {
  return publication.publish(snapshot, () => {
    domainBody = result.body;
    domainMeta = result.meta;
    alerts = result.alerts;
    openQuestion = result.question;
    questionInbox = result.inbox;
    if (result.pendingQuestions !== undefined) { pendingQuestions = result.pendingQuestions; renderCounts(); }
    if (openQuestion) renderQuestion();
    else if (questionInbox) renderQuestionInbox();
    else renderDomain();
  });
}

async function loadDomainDestination(target: Destination): Promise<void> {
  publication.navigate();
  const snapshot = publication.capture(true);
  const api = hostedApi(snapshot);
  const heldAlerts = alerts;
  commitDomain(snapshot, { body: `<p class="muted">Loading…</p>`, meta: "", alerts: heldAlerts, question: null, inbox: null });
  const result = await readDomainDestination(target, api, heldAlerts);
  if (!commitDomain(snapshot, result) || !result.question) return;
  await refreshQuestionThread(snapshot, result.question.request, api);
}

function renderDomain(): void {
  const showing = showsDomainView(destination);
  select<HTMLElement>("[data-domain]").hidden = !showing;
  if (!showing) return;
  select("[data-domain-breadcrumb]").innerHTML = breadcrumbHtml(formatDestination(destination));
  select("[data-domain-title]").textContent = destinationTitle(destination);
  select("[data-domain-meta]").textContent = domainMeta;
  select("[data-domain-pill]").textContent = destination.kind === "alerts" ? `${alerts.length} live` : "";
  select("[data-domain-body]").innerHTML = domainBody;
}

/**
 * Records a decision about an alert and tries to send it. The decision is
 * durable either way — a failure leaves it queued and visible, never silently
 * applied to this browser's view alone.
 */
async function actOnAlert(alertId: string, action: AlertAction): Promise<void> {
  const snapshot = publication.capture();
  const alert = alerts.find((candidate) => candidate.alertId === alertId);
  const subject = alert ? alertDestination(alert) : ({ kind: "alert", id: alertId } as Destination);
  const snoozeSeconds = action === "snooze" ? 3600 : undefined;
  intents = enqueueIntent(browserStore(), intents, { ownerAccountId: accountId, alertId, action, subject, ...(snoozeSeconds ? { snoozeSeconds } : {}) });
  renderAlertDecisions(snapshot);
  await flushIntents();
  renderAlertDecisions(snapshot);
}

function renderAlertDecisions(snapshot: Publication): void {
  publication.publish(snapshot, () => {
    if ((destination.kind === "alerts" || destination.kind === "alert-history") && alerts.length > 0) {
      domainBody = alerts.map((alert) => alertRow(alert, intents)).join("");
    }
    renderDomain();
  });
}

/**
 * Sends every decision the hosted vault has not accepted yet.
 *
 * `singleFlight` is what stops `actOnAlert` and a visibility-driven `reconcile`
 * from POSTing the same pending intent twice.
 */
const flushIntents = singleFlight(async () => {
  const snapshot = publication.capture();
  const api = hostedApi(snapshot);
  for (const intent of pendingIntents(intents)) {
    if (!publication.current(snapshot) || intent.ownerAccountId !== snapshot.accountId) return;
    const result = await postAlertAction(api, intent.alertId, intent.action, intent.snoozeSeconds);
    if (!publication.publish(snapshot, () => {
      intents = result.ok ? markIntentSent(browserStore(), intents, intent.id)
        : markIntentFailed(browserStore(), intents, intent.id, result.detail);
    })) return;
  }
});

/**
 * Foreground reconciliation. Locally derived badges are a guess; coming back to
 * the tab is the moment to replace them with what the server actually counts.
 */
/**
 * The tenant-wide outstanding count behind the Questions badge.
 *
 * Read from the listing endpoint, which reports it independently of any filter,
 * so one row is enough to learn the whole number. Held as `null` until a read
 * succeeds, because a badge showing zero when nothing has been read yet would
 * assert that nothing is owed.
 */
let pendingQuestions: number | null = null;

async function refreshQuestionCount(): Promise<void> {
  const snapshot = publication.capture();
  const result = await fetchLedgerRequests(hostedApi(snapshot), {
    direction: INBOX_DIRECTION, states: INBOX_STATES, limit: 1,
  });
  if (!result.ok) return;
  publication.publish(snapshot, () => { pendingQuestions = result.value.pending; renderCounts(); });
}

async function reconcile(): Promise<void> {
  const snapshot = publication.capture();
  run(refreshQuestionCount());
  const result = await fetchCounts(hostedApi(snapshot));
  if (!publication.publish(snapshot, () => {
    counts = reconcileCounts(derivedCounts(notes), result.ok ? parseServerCounts(result.value) : undefined);
    if (result.ok) mcpActivity = parseMcpActivity(result.value);
    renderCounts();
    renderMcpActivity();
  })) return;
  await flushIntents();
  if (!publication.current(snapshot)) return;
  if (destination.kind === "alerts" || destination.kind === "alert-history") await loadDomainDestination(destination);
}

function renderCounts(): void {
  select("[data-note-count]").textContent = String(counts.counts.notes);
  select("[data-queue-count]").textContent = String(counts.counts.queued);
  select("[data-conflict-count]").textContent = String(counts.counts.conflicts);
  select("[data-alert-count]").textContent = String(counts.counts.liveAlerts);
  select("[data-question-count]").textContent = pendingQuestions === null ? "—" : String(pendingQuestions);
  select("[data-source-count]").textContent = String(counts.counts.unhealthySources);
  select("[data-counts-note]").textContent = countsLabel(counts);
}

/** The same fact in both places it matters: under the empty state, and beside the account. */
function renderMcpActivity(): void {
  const label = mcpActivityLabel(mcpActivity);
  for (const selector of ["[data-ai-activity]", "[data-account-activity]"]) {
    const element = select<HTMLElement>(selector);
    element.textContent = label;
    element.hidden = label === "";
  }
}

/* ------------------------------------------------------------------ rails */

/**
 * Below this width the layout has no rails to collapse: the sidebar *is* the
 * screen, and hiding it would leave a reader with nothing to navigate. The
 * remembered preference is kept, simply not applied.
 */
const railLayout = window.matchMedia("(min-width: 721px)");

function applyRails(): void {
  const layoutHasRails = railLayout.matches;
  appFrame.dataset["navRail"] = rails.navCollapsed ? "collapsed" : "open";
  appFrame.dataset["contextRail"] = rails.contextCollapsed ? "collapsed" : "open";
  // `hidden` rather than a CSS rule: a rail the reader has put away must leave
  // the tab order and the accessibility tree, not just the picture.
  select<HTMLElement>("#vault-rail").hidden = layoutHasRails && rails.navCollapsed;
  select<HTMLElement>("#context-rail").hidden = layoutHasRails && rails.contextCollapsed;
  for (const [action, collapsed, label] of [
    ["toggle-nav-rail", rails.navCollapsed, "notes rail"],
    ["toggle-context-rail", rails.contextCollapsed, "context rail"],
  ] as const) {
    const button = select<HTMLButtonElement>(`[data-action='${action}']`);
    button.setAttribute("aria-expanded", String(!collapsed));
    button.setAttribute("aria-label", `${collapsed ? "Show" : "Hide"} the ${label}`);
    button.classList.toggle("collapsed", collapsed);
  }
}

function setRails(next: RailState): void {
  rails = next;
  writeRailState(browserStore(), rails);
  applyRails();
}

railLayout.addEventListener("change", applyRails);

rails = readRailState(browserStore());
applyRails();

/* ---------------------------------------------------------------- routing */

/**
 * Moves to a destination by writing its address. The `hashchange` listener does
 * the rendering, so the back button and a typed URL take exactly the same path
 * as a click.
 */
function navigate(next: Destination): void {
  const href = webHref(next);
  if (location.hash === href) {
    applyDestination();
    return;
  }
  location.hash = href;
}

/** Reads the address bar, tolerating an unrecognised link rather than breaking. */
function destinationFromLocation(): Destination {
  const parsed = parseDestination(location.hash);
  if (parsed) return parsed;
  showToast("That link does not point anywhere in this vault.");
  return DEFAULT_DESTINATION;
}

function applyDestination(): void {
  publication.navigate();
  destination = destinationFromLocation();
  // Before sign-in there is no vault worker and no session, so the address is
  // remembered but nothing is loaded from it yet.
  if (!accountId) return;
  // Every destination change lands here — `navigate` either writes the hash for
  // the `hashchange` listener or calls this directly — so the phone's workspace
  // layer is decided once, for notes and domain objects alike.
  applyMobileWorkspace(document.body, destination);
  switch (destination.kind) {
    case "vault":
      filter = destination.filter;
      selectedId = "";
      break;
    case "note":
      // A note id from a link may name a note this device has not synced yet;
      // the reader says so rather than the address silently changing.
      selectedId = destination.id;
      break;
    case "search":
      searchInput.value = destination.query;
      setSearchMode(destination.mode, false);
      if (!searchDialog.open) searchDialog.showModal();
      run(runSearch());
      break;
    case "account":
      if (!accountDialog.open) accountDialog.showModal();
      run(extensionPanel.load());
      break;
    default:
      selectedId = "";
      run(loadDomainDestination(destination));
      break;
  }
  render();
  document.querySelectorAll<HTMLElement>("[data-go]").forEach((item) => {
    const target = parseDestination(item.dataset.go ?? "");
    item.classList.toggle("active", Boolean(target) && sameDestination(target!, destination));
  });
}

function renderCapabilities(): void {
  if (!capabilities) return;
  const storageLabel = capabilities.storage === "indexeddb+opfs" ? "IndexedDB + OPFS mirror" : capabilities.storage === "indexeddb" ? "IndexedDB fallback" : "Memory only";
  const coreLabel = "Rust WASM core";
  select("[data-capabilities]").innerHTML = `<span class="status-dot ${capabilities.storage === "memory" ? "warn" : ""}"></span><div><strong>${storageLabel}</strong><p>${coreLabel} · ${capabilities.persistent ? "eviction protected" : "protection not granted"}</p></div>`;
  select("[data-account-storage]").innerHTML = `
    <div><span>Storage engine</span><strong>${storageLabel}</strong></div>
    <div><span>Core engine</span><strong>${coreLabel}</strong></div>
    <div><span>Eviction protection</span><strong>${capabilities.persistent ? "Granted" : "Not yet granted"}</strong></div>
    ${capabilities.detail ? `<p>${escapeHtml(capabilities.detail)}</p>` : ""}`;
}

function renderBacklinks(note: VaultNote): void {
  const linked = backlinks(notes, note);
  select("[data-backlink-count]").textContent = String(linked.length);
  select("[data-backlinks]").innerHTML = linked.length === 0
    ? `<p class="muted">No notes link here yet. Ask your AI to add <code>[[${escapeHtml(note.title)}]]</code> where it belongs.</p>`
    : linked.map((item) => `<button class="backlink" type="button" data-note="${escapeHtml(item.id)}"><strong>${escapeHtml(item.title)}</strong><span>${escapeHtml(item.body.slice(0, 86))}</span></button>`).join("");
  if (note.state === "conflict") {
    select("[data-backlinks]").insertAdjacentHTML("afterbegin", `<div class="conflict-card"><strong>A remote edit also changed this note.</strong><p>Both copies are preserved. Keep one, or ask your AI to merge them before you choose.</p><button type="button" data-action="keep-local">Keep local</button><button type="button" data-action="keep-remote">Use remote</button><button type="button" class="conflict-view" data-action="view-conflict">See the remote text</button></div>`);
  }
}

function breadcrumbHtml(path: string): string {
  return path.split("/").map((part, index, parts) => `<span>${escapeHtml(part)}</span>${index < parts.length - 1 ? "<b>›</b>" : ""}`).join("");
}

function renderReader(): void {
  const note = selected();
  const noNotes = notes.length === 0;
  const onDomainObject = !["vault", "note", "search", "account"].includes(destination.kind);
  select<HTMLElement>("[data-welcome]").hidden = Boolean(note) || onDomainObject;
  select<HTMLElement>("[data-reader]").hidden = !note || onDomainObject;
  const welcome = noNotes ? startupWelcome(startupState) : undefined;
  select("[data-welcome-title]").textContent = welcome?.title ?? (noNotes ? "Nothing written yet." : "Choose a note.");
  select("[data-welcome-copy]").textContent = welcome?.copy ?? (noNotes
    ? AUTHORSHIP_LINE
    : "Pick a note on the left, or ask your AI for a new one.");
  select("[data-freshness]").textContent = freshnessLabel(vaultPulse(notes, receipts));
  if (!note) return;
  select("[data-breadcrumb]").innerHTML = breadcrumbHtml(note.path);
  const parsed = readNote(note.body, note.path);
  select("[data-reader-title]").textContent = parsed.title;
  renderFrontmatter(parsed.metadata);
  const pulse = `${stateLabel(note.state)} · updated ${new Date(note.updatedAt).toLocaleString()}`;
  select("[data-reader-meta]").textContent = `${note.path} · ${pulse}`;
  const pill = select<HTMLElement>("[data-state-pill]");
  pill.textContent = stateLabel(note.state);
  pill.className = `state-pill ${note.state}`;
  renderPreview(parsed.content);
  sourceView.textContent = note.body;
  preview.hidden = showingSource;
  sourceView.hidden = !showingSource;
  select<HTMLButtonElement>("[data-action='toggle-source']").setAttribute("aria-pressed", String(showingSource));
  select("[data-read-message]").textContent = note.state === "conflict"
    ? "Two versions of this note disagree. Resolve it in Connected notes."
    : freshnessLabel(vaultPulse(notes, receipts));
  renderBacklinks(note);
  const store = browserStore();
  try { store?.setItem(`recallvault.selected:${accountId}`, note.id); } catch { /* private mode */ }
}

/**
 * Shows the handful of frontmatter keys a reader cares about. The rest stays
 * out of sight: the defect being fixed was a screen of YAML, and reprinting all
 * of it under a nicer heading would be the same screen.
 */
function renderFrontmatter(metadata: ReadonlyMap<string, string>): void {
  const fields = headerMetadata(metadata);
  const list = select<HTMLElement>("[data-reader-frontmatter]");
  list.hidden = fields.length === 0;
  list.innerHTML = fields
    .map(({ key, value }) => `<div><dt>${escapeHtml(key)}</dt><dd>${escapeHtml(value)}</dd></div>`)
    .join("");
}

function renderPreview(body: string): void {
  preview.innerHTML = renderMarkdown(body.replace(/```artifact-html\s*\n[\s\S]*?```/g, "\n\n> Active artifact shown below in an isolated frame.\n\n"));
  for (const artifact of activeArtifacts(body)) preview.append(createIsolatedArtifact(artifact));
}

function render(): void {
  // A vault with readable cached notes must never present itself as empty just
  // because a remembered selection disappeared or the user opened All notes.
  if (destination.kind === "vault" && !selected() && notes.length > 0) {
    selectedId = vaultSelection(notes, selectedId, filteredNotes());
  }
  counts = reconcileCounts(derivedCounts(notes), counts.origin === "authoritative" ? counts.counts : undefined, counts.reconciledAt);
  renderNoteList();
  renderCounts();
  renderDomain();
  renderReader();
  renderReceipts();
  renderActivity();
  renderCapabilities();
  const pulse = vaultPulse(notes, receipts);
  const status = syncStatus(pulse, syncing);
  select("[data-sync-label]").textContent = status.buttonLabel;
  const connection = select<HTMLElement>("[data-connection-state]");
  const override = connectionOverride(startupState, offlineStart);
  connection.textContent = connectionLabelWithDurability(override?.label ?? status.connectionLabel, capabilities?.persistent);
  connection.className = `connection-state ${override?.state ?? status.state}`;
  connection.title = freshnessLabel(pulse);
  select("[data-account-title]").textContent = status.connectionLabel;
}

async function initialize(): Promise<void> {
  const snapshot = publication.capture();
  const currentClient = client;
  let result: InitResult;
  try { result = await currentClient.request<InitResult>({ type: "init", accountId }); }
  catch (error) { if (!publication.current(snapshot)) return; throw error; }
  publication.publish(snapshot, () => {
    notes = [...result.notes];
    receipts = [...result.receipts].sort((a, b) => b.at.localeCompare(a.at));
    capabilities = result.capabilities;
    if (!notes.some(({ id }) => id === selectedId)) selectedId = notes[0]?.id ?? "";
    render();
    trace("vault opened offline");
  });
}

/**
 * Re-reads the shared store after another tab changed it. The list and the
 * open reader both follow, because `render` derives everything from `notes`.
 */
async function refreshFromStore(): Promise<void> {
  const snapshot = publication.capture();
  const [list, latest] = await Promise.all([
    client.request<readonly VaultNote[]>({ type: "list" }),
    client.request<readonly SyncReceipt[]>({ type: "receipts" }),
  ]);
  publication.publish(snapshot, () => { notes = [...list]; receipts = [...latest]; render(); });
}

async function selectNote(id: string): Promise<void> {
  showingSource = false;
  // `navigate` routes through `applyDestination`, which brings the workspace
  // forward at phone width.
  navigate({ kind: "note", id });
  select<HTMLElement>("#reader").focus();
  trace(`read ${selected()?.path ?? id}`);
}

function openAsk(kind: AskKind, subject: string): void {
  const ask = askPrompt(kind, subject, today());
  select("[data-ask-title]").textContent = ask.title;
  select("[data-ask-explanation]").textContent = ask.explanation;
  askPromptField.value = ask.prompt;
  run(composer.open(accountId, { body: ask.prompt, subjectRef: subject.trim() || suggestedPath("new note", today()),
    reason: ask.title, originRef: accountId, idempotencyKey: crypto.randomUUID() }));
  select("[data-ask-copied]").textContent = "";
  if (!askDialog.open) askDialog.showModal();
  askPromptField.focus();
  askPromptField.select();
}

async function submitAsk(): Promise<void> {
  await composer.submit();
}

async function copyPrompt(): Promise<void> {
  const status = select("[data-ask-copied]");
  let text: string;
  try { text = composer.copyText(); }
  catch (error) { status.textContent = error instanceof Error ? error.message : "The attachment is not ready to copy."; return; }
  try {
    await navigator.clipboard.writeText(text);
    status.textContent = "Copied — paste it to your assistant.";
  } catch {
    askPromptField.value = text;
    askPromptField.select();
    status.textContent = "Copy blocked by the browser; the prompt is selected for you.";
  }
}

function normalizedPath(raw: string): string {
  const pieces = raw.trim().replaceAll("\\", "/").split("/");
  const output: string[] = [];
  for (const piece of pieces) {
    if (!piece || piece === ".") continue;
    if (piece === "..") {
      if (!output.pop()) throw new Error("A note path cannot leave the vault.");
    } else output.push(piece);
  }
  if (output.length === 0) throw new Error("Give this note a path.");
  const joined = output.join("/");
  return joined.endsWith(".md") ? joined : `${joined}.md`;
}

async function syncNow(): Promise<boolean> {
  const snapshot = publication.capture();
  const currentClient = client;
  if (!publication.current(snapshot)) return false;
  syncing = true;
  render();
  select("[data-sync-icon]").classList.add("spinning");
  trace("sync started");
  try {
    const result = await currentClient.request<{ notes: readonly VaultNote[]; receipt: SyncReceipt }>({ type: "sync" });
    if (!publication.current(snapshot)) return false;
    const arriving = vaultPulse([...result.notes], [result.receipt, ...receipts]).arriving;
    notes = [...result.notes];
    receipts = [result.receipt, ...receipts.filter(({ id }) => id !== result.receipt.id)];
    render();
    showToast(result.receipt.outcome === "synced" && arriving > 0
      ? `${arriving} note${arriving === 1 ? "" : "s"} from your AI are now readable here`
      : result.receipt.detail);
    trace(`sync ${result.receipt.outcome}`);
    if (result.receipt.authCode) {
      await handleAuthFailure(result.receipt.authCode);
      return false;
    }
    tabChannel?.postMessage({ type: "changed", accountId });
    if (result.receipt.outcome === "synced") {
      offlineStart = false;
      if (startupState === "unreachable") startupState = "restored";
      await protectStorageOnce();
    }
    return result.receipt.outcome !== "failed";
  } catch (error) {
    if (!publication.current(snapshot)) return false;
    throw error;
  } finally {
    publication.publish(snapshot, () => { syncing = false; render(); select("[data-sync-icon]").classList.remove("spinning"); });
  }
}

async function openSemanticResult(path: string): Promise<void> {
  if (!await syncNow()) return;
  const logical = path.replace(/^notes\//, "");
  const note = notes.find((candidate) => candidate.path === logical);
  if (!note) {
    showToast("That result could not be materialized from the hosted vault.");
    return;
  }
  await selectNote(note.id);
}

async function runSearch(): Promise<void> {
  const snapshot = publication.capture(true);
  const query = searchInput.value.trim();
  const status = select("[data-search-status]");
  status.classList.remove("recalled");
  if (!query) {
    status.textContent = searchMode === "exact" ? "Type to search titles, paths, and Markdown." : "Describe what you remember. This query is sent to hosted search.";
    searchResults.innerHTML = "";
    return;
  }
  status.textContent = searchMode === "exact" ? "Searching this device…" : "Asking hosted semantic search…";
  try {
    let results: readonly VaultNote[] = [];
    if (searchMode === "exact") {
      results = await client.request<readonly VaultNote[]>({ type: "search", query });
    } else {
      const response = await apiFetch(hostedApi(snapshot), `/v1/search/semantic?q=${encodeURIComponent(query)}`, { credentials: "include", headers: CLIENT_HEADERS });
      if (!response.ok) throw new Error(response.status === 401 ? "Connect hosted sync to use semantic search." : `Hosted search returned ${response.status}.`);
      const payload = await response.json() as { readonly results?: readonly VaultNote[] };
      results = payload.results ?? [];
    }
    publication.publish(snapshot, () => {
      status.textContent = `${results.length} result${results.length === 1 ? "" : "s"} · ${searchMode === "exact" ? "on this device" : "ranked online"}`;
      status.classList.toggle("recalled", searchMode === "semantic" && results.length > 0);
      const miss = searchMiss(notes.length === 0);
      searchResults.innerHTML = results.length === 0 ? `<div class="search-empty"><strong>${escapeHtml(miss.headline)}</strong><span>${escapeHtml(miss.detail)}</span><button type="button" data-action="ask-search">Ask your AI about “${escapeHtml(query)}”</button></div>` : results.map((note) => `
        <button class="search-result" type="button" ${searchMode === "semantic" ? `data-semantic-path="${escapeHtml(note.path)}"` : `data-note="${escapeHtml(note.id)}"`}><span class="note-glyph">≡</span><span><strong>${escapeHtml(note.title)}</strong><small>${escapeHtml(note.path)}</small><p>${escapeHtml(note.body.replace(/^#+\s*/m, "").slice(0, 150))}</p></span><i>${stateLabel(note.state)}</i></button>
      `).join("");
      trace(`${searchMode} search`);
    });
  } catch (error) {
    publication.publish(snapshot, () => {
      status.textContent = error instanceof Error ? error.message : "Search unavailable";
      searchResults.innerHTML = `<div class="search-empty"><strong>Online search is unavailable.</strong><span>Your exact local search and notes still work.</span></div>`;
    });
  }
}

async function showHistory(): Promise<void> {
  const snapshot = publication.capture(true);
  const note = selected();
  if (!note) return;
  const history = await client.request<readonly NoteVersion[]>({ type: "history", noteId: note.id });
  publication.publish(snapshot, () => {
    select("[data-history-list]").innerHTML = history.length === 0 ? `<div class="search-empty"><strong>No earlier saves.</strong><span>Versions appear here as your AI changes this note.</span></div>` : history.map((version) => `
      <div class="history-row"><div><strong>${new Date(version.savedAt).toLocaleString()}</strong><span>${escapeHtml(version.reason)} · ${version.body.length} characters</span></div><button type="button" data-restore="${escapeHtml(version.id)}">Restore</button><template data-version="${escapeHtml(version.id)}">${escapeHtml(version.body)}</template></div>
    `).join("");
    historyDialog.showModal();
  });
}

async function restoreVersion(versionId: string): Promise<void> {
  const snapshot = publication.capture(true);
  const note = selected();
  const template = document.querySelector<HTMLTemplateElement>(`template[data-version="${CSS.escape(versionId)}"]`);
  if (!note || !template) return;
  const body = template.content.textContent ?? "";
  const saved = await client.request<VaultNote>({ type: "save", reason: "resolution", baseEtag: note.etag, note: { ...note, body } });
  publication.publish(snapshot, () => {
    notes = [saved, ...notes.filter(({ id }) => id !== saved.id)];
    historyDialog.close();
    render();
    showToast("Earlier text restored as a new version");
    trace(`restored ${saved.path}`);
    tabChannel?.postMessage({ type: "changed", accountId });
  });
}

/**
 * The first successful sync is when the local copy becomes worth protecting.
 * Asked once per launch; a refusal shows in the sync state rather than nagging.
 */
async function protectStorageOnce(): Promise<void> {
  if (storageRequested || capabilities?.persistent) return;
  storageRequested = true;
  const persistent = await ensureDurableStorage(navigator.storage);
  capabilities = capabilities ? { ...capabilities, persistent } : capabilities;
  render();
}

async function persistStorage(): Promise<void> {
  const granted = await navigator.storage.persist().catch(() => false);
  capabilities = capabilities ? { ...capabilities, persistent: granted || capabilities.persistent } : capabilities;
  renderCapabilities();
  showToast(granted ? "Offline storage is now eviction protected" : "The browser did not grant protection yet");
}

function exportVault(): void {
  const payload = JSON.stringify({ format: "recallvault-export", version: 1, exportedAt: new Date().toISOString(), notes }, null, 2);
  const url = URL.createObjectURL(new Blob([payload], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `recallvault-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
  showToast(`${notes.length} note${notes.length === 1 ? "" : "s"} exported`);
}

async function importFiles(files: FileList): Promise<void> {
  const snapshot = publication.capture();
  const currentClient = client;
  let imported = 0;
  for (const file of [...files]) {
    if (file.size > 5 * 1024 * 1024) throw new Error(`${file.name} is larger than the 5 MB import limit.`);
    const text = await file.text();
    if (!publication.current(snapshot)) return;
    const candidates: { readonly path: string; readonly title?: string; readonly body: string }[] = [];
    if (file.name.toLocaleLowerCase().endsWith(".json")) {
      const parsed = JSON.parse(text) as { readonly format?: string; readonly notes?: readonly unknown[] };
      if (parsed.format !== "recallvault-export" || !Array.isArray(parsed.notes)) throw new Error(`${file.name} is not a RecallVault export.`);
      for (const value of parsed.notes) {
        if (typeof value !== "object" || value === null) continue;
        const record = value as Record<string, unknown>;
        if (typeof record.path === "string" && typeof record.body === "string") {
          candidates.push({ path: normalizedPath(record.path), body: record.body, title: typeof record.title === "string" ? record.title : undefined });
        }
      }
    } else {
      candidates.push({ path: normalizedPath(file.name), body: text });
    }
    for (const candidate of candidates) {
      const existing = notes.find(({ path }) => path === candidate.path);
      const note: VaultNote = {
        id: existing?.id ?? makeId("note"), path: candidate.path,
        title: candidate.title?.trim() || noteTitle(candidate.body, candidate.path), body: candidate.body,
        updatedAt: new Date().toISOString(), etag: await makeEtag(candidate.body), state: "queued",
      };
      if (!publication.current(snapshot)) return;
      const saved = await currentClient.request<VaultNote>({ type: "save", note, reason: "import" });
      if (!publication.publish(snapshot, () => { notes = [saved, ...notes.filter(({ id }) => id !== saved.id)]; })) return;
      imported += 1;
    }
  }
  render();
  tabChannel?.postMessage({ type: "changed", accountId });
  showToast(`${imported} note${imported === 1 ? "" : "s"} imported and queued`);
}

/**
 * Reacts to a server refusal by its code rather than its prose.
 *
 * `not_invited` is terminal: the same identity will be refused every time, so
 * re-arming the provider buttons would loop the user through sign-in forever.
 */
async function handleAuthFailure(code: AuthFailureCode): Promise<void> {
  endWebSession().close();
  client = new VaultWorkerClient();
  forgetSessionValidation(browserStore());
  broadcastSession(null);
  await composer.reset();
  askDialog.close();
  appFrame.hidden = true;
  authGate.hidden = false;
  accountDialog.close();
  if (code === "not_invited") {
    showTerminalAuthFailure("This address is not invited to RecallVault. Signing in again will not change that — ask the operator for an invite, then reload.");
    return;
  }
  authStatus.textContent = code === "forbidden_origin"
    ? "This browser origin is not allowed to reach RecallVault. Open the app from its own address."
    : "Your session expired. Sign in again; queued changes remain safely on this browser.";
  await initializeProviderButtons();
}

/** A dead end: no provider buttons, no retry, because neither can succeed. */
function showTerminalAuthFailure(message: string): void {
  select<HTMLElement>("[data-provider-buttons]").hidden = true;
  select<HTMLButtonElement>("[data-action='retry-auth']").hidden = true;
  authStatus.textContent = message;
}

function broadcastSession(account: string | null): void {
  const event = { type: "session", accountId: account, nonce: crypto.randomUUID() };
  tabChannel?.postMessage(event);
  try { browserStore()?.setItem("recallvault.sessionEvent", JSON.stringify(event)); } catch { /* storage unavailable */ }
}

function endWebSession(): VaultWorkerClient {
  publication.end();
  const previous = client;
  accountId = "";
  notes = [];
  receipts = [];
  intents = [];
  alerts = [];
  capabilities = undefined;
  selectedId = "";
  expandedFolders = new Set();
  domainBody = "";
  domainMeta = "";
  openQuestion = null;
  questionInbox = null;
  pendingQuestions = null;
  mcpActivity = undefined;
  counts = reconcileCounts(derivedCounts([]), undefined);
  syncing = false;
  searchInput.value = "";
  searchResults.innerHTML = "";
  traceEvents.length = 0;
  select("[data-trace]").innerHTML = "";
  select("[data-history-list]").innerHTML = "";
  toast.textContent = "";
  sourceView.textContent = "";
  preview.innerHTML = "";
  for (const selector of ["[data-reader-title]", "[data-breadcrumb]", "[data-reader-frontmatter]",
    "[data-reader-meta]", "[data-state-pill]", "[data-backlinks]", "[data-account-storage]", "[data-capabilities]"]) {
    select(selector).textContent = "";
  }
  select(".avatar").textContent = "R";
  extensionPanel.reset();
  searchDialog.close();
  historyDialog.close();
  accountDialog.close();
  askDialog.close();
  appFrame.hidden = true;
  authGate.hidden = false;
  render();
  return previous;
}

async function switchToLocal(): Promise<void> {
  const previousAccount = accountId;
  const previous = endWebSession();
  broadcastSession(null);
  forgetLocalAccount(browserStore(), previousAccount);
  await composer.reset();
  await signOutBrowserSession(API_ORIGIN).catch(() => undefined);
  // The worker is fenced from UI publication, but keeps its handles until erasure finishes.
  await previous.request<void>({ type: "erase" }).catch(() => undefined);
  previous.close();
  client = new VaultWorkerClient();
  authStatus.textContent = "Signed out. This browser's copy of the vault was erased.";
  await initializeProviderButtons();
}

async function resolveConflict(useRemote: boolean): Promise<void> {
  const snapshot = publication.capture(true);
  const note = selected();
  if (!note?.conflictBody) return;
  const body = useRemote ? note.conflictBody : note.body;
  const saved = await client.request<VaultNote>({ type: "save", reason: "resolution", baseEtag: note.etag, note: { ...note, body, conflictBody: undefined, state: "queued" } });
  publication.publish(snapshot, () => {
    notes = [saved, ...notes.filter(({ id }) => id !== saved.id)];
    render();
    showToast(saved.state === "conflict" ? "Another tab changed this note first; both copies are kept" : "Resolution saved and queued");
    trace(`conflict resolved ${saved.path}`);
    tabChannel?.postMessage({ type: "changed", accountId });
  });
}

function viewConflictBody(): void {
  const note = selected();
  if (!note?.conflictBody) return;
  showingSource = true;
  sourceView.textContent = note.conflictBody;
  preview.hidden = true;
  sourceView.hidden = false;
  select("[data-read-message]").textContent = "Showing the remote text. Toggle the source button to return to your copy.";
}

document.addEventListener("click", (event) => {
  const target = event.target as Element;
  const semanticResult = target.closest<HTMLElement>("[data-semantic-path]");
  if (semanticResult?.dataset.semanticPath) {
    run(openSemanticResult(semanticResult.dataset.semanticPath));
    return;
  }
  // Tree items nest, so the nearest item decides — not the folder around it.
  const treeItem = target.closest<HTMLElement>("[data-row]");
  if (treeItem) {
    if (treeItem.dataset.note) { run(selectNote(treeItem.dataset.note)); return; }
    if (treeItem.dataset["folder"] !== undefined) { toggleFolder(treeItem.dataset["folder"]); return; }
  }
  const noteButton = target.closest<HTMLElement>("[data-note]");
  if (noteButton?.dataset.note) {
    run(selectNote(noteButton.dataset.note));
    searchDialog.close();
    return;
  }
  const wiki = target.closest<HTMLElement>("[data-wikilink]");
  if (wiki?.dataset.wikilink) {
    const wanted = wiki.dataset.wikilink.toLocaleLowerCase();
    const found = notes.find((note) => note.title.toLocaleLowerCase() === wanted || note.path.replace(/\.md$/i, "").toLocaleLowerCase() === wanted);
    if (found) run(selectNote(found.id));
    else openAsk("missing-link", wiki.dataset.wikilink);
    return;
  }
  const go = target.closest<HTMLElement>("[data-go]")?.dataset.go;
  if (go !== undefined) {
    const next = parseDestination(go);
    if (next) navigate(next);
    else showToast("That destination is not one this client knows.");
    searchDialog.close();
    return;
  }
  const choice = target.closest<HTMLElement>("[data-answer-choice]")?.dataset["answerChoice"];
  if (choice !== undefined && openQuestion !== null) {
    // A second click on the chosen answer clears it, so a mis-click is undone
    // rather than being committed by the next Submit.
    const already = (openQuestion.draft.choiceId ?? "") === choice;
    openQuestion = {
      ...openQuestion,
      draft: { ...openQuestion.draft, choiceId: already ? null : choice },
      error: null,
    };
    renderQuestion();
    return;
  }
  if (target.closest<HTMLElement>("[data-questions-more]") !== null) {
    run(readMoreQuestions());
    return;
  }
  if (target.closest<HTMLElement>("[data-answer-submit]") !== null) {
    run(submitAnswer());
    return;
  }
  const alertButton = target.closest<HTMLElement>("[data-alert-action]");
  if (alertButton?.dataset.alert && alertButton.dataset.alertAction) {
    run(actOnAlert(alertButton.dataset.alert, alertButton.dataset.alertAction as AlertAction));
    return;
  }
  const mode = target.closest<HTMLElement>("[data-search-mode]")?.dataset.searchMode as typeof searchMode | undefined;
  if (mode) {
    setSearchMode(mode);
    return;
  }
  const restore = target.closest<HTMLElement>("[data-restore]");
  if (restore?.dataset.restore) {
    void restoreVersion(restore.dataset.restore).catch((error: unknown) => showToast(error instanceof Error ? error.message : "Restore failed"));
    return;
  }
  const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
  switch (action) {
    // The phone's Vault button goes to the vault rather than stripping the
    // class behind the address bar's back, so the view and the URL agree. It
    // keeps the filter the list was showing; going back is not a reset.
    case "home": navigate({ kind: "vault", filter }); break;
    case "toggle-nav-rail": setRails({ ...rails, navCollapsed: !rails.navCollapsed }); break;
    case "toggle-context-rail": setRails({ ...rails, contextCollapsed: !rails.contextCollapsed }); break;
    case "collapse-folders":
      expandedFolders = new Set();
      writeExpandedFolders(browserStore(), accountId, expandedFolders);
      renderNoteList();
      break;
    case "ask": openAsk("create", searchDialog.open ? searchInput.value.trim() : ""); searchDialog.close(); break;
    case "ask-revise": openAsk("revise", selected()?.path ?? ""); break;
    case "ask-search": openAsk("search-miss", searchInput.value.trim()); searchDialog.close(); break;
    case "submit-ask": run(submitAsk()); break;
    case "copy-prompt": run(copyPrompt()); break;
    case "sync": run(syncNow()); break;
    case "open-search": navigate({ kind: "search", query: searchInput.value.trim(), mode: "exact" }); window.setTimeout(() => searchInput.focus(), 0); break;
    case "toggle-source": {
      showingSource = !showingSource;
      renderReader();
      break;
    }
    case "history": run(showHistory()); break;
    case "account":
      navigate({ kind: "account" });
      if (googleClientId) void initializeGoogleButton("link").catch(showLinkError);
      break;
    case "reload-update": location.reload(); break;
    case "dismiss-update": dismissUpdate(); break;
    case "discard-shell": run(discardCachedShell()); break;
    case "enable-push": run(turnOnNotifications()); break;
    case "persist": run(persistStorage()); break;
    case "export": exportVault(); break;
    case "import": select<HTMLInputElement>("[data-import-input]").click(); break;
    case "link-apple": void signInWithApple("link").catch(showLinkError); break;
    case "logout": run(switchToLocal()); break;
    case "keep-local": run(resolveConflict(false)); break;
    case "keep-remote": run(resolveConflict(true)); break;
    case "view-conflict": viewConflictBody(); break;
    case "install": if (deferredInstall && "prompt" in deferredInstall) void (deferredInstall as Event & { prompt(): Promise<void> }).prompt(); break;
  }
});

// Closing a dialog that *is* a destination has to move the address too, or the
// URL would keep claiming the user is somewhere they have left.
for (const [dialog, kind] of [[searchDialog, "search"], [accountDialog, "account"]] as const) {
  dialog.addEventListener("close", () => {
    if (destination.kind === kind) navigate(DEFAULT_DESTINATION);
  });
}

select<HTMLInputElement>("[data-import-input]").addEventListener("change", (event) => {
  const input = event.currentTarget as HTMLInputElement;
  if (input.files) void importFiles(input.files).catch((error: unknown) => showToast(error instanceof Error ? error.message : "Import failed"));
  input.value = "";
});

function acceptSessionSignal(event: { readonly type?: string; readonly accountId?: string | null }): void {
  if (event.type !== "session" || !accountId || event.accountId === accountId) return;
  const previous = endWebSession();
  previous.close();
  client = new VaultWorkerClient();
  run(composer.reset());
  authStatus.textContent = "The active account changed in another tab. Sign in again to open its vault.";
  run(initializeProviderButtons());
}

tabChannel?.addEventListener("message", (event: MessageEvent<{ readonly type?: string; readonly accountId?: string | null }>) => {
  if (event.data.type === "session") { acceptSessionSignal(event.data); return; }
  if (event.data.type !== "changed" || event.data.accountId !== accountId) return;
  run(refreshFromStore().then(() => showToast("Vault refreshed from another tab")));
});
window.addEventListener("storage", (event) => {
  if (event.key !== "recallvault.sessionEvent" || !event.newValue) return;
  try { acceptSessionSignal(JSON.parse(event.newValue)); } catch { /* invalid external signal */ }
});

// The typed part of an answer is kept in state as it is typed, so a redraw
// caused by picking a choice never discards a half-written reply.
document.addEventListener("input", (event) => {
  const field = event.target;
  if (!(field instanceof HTMLTextAreaElement) || field.dataset["answerText"] === undefined) return;
  if (openQuestion === null) return;
  openQuestion = { ...openQuestion, draft: { ...openQuestion.draft, text: field.value } };
});

// Submitting the form, which is what Enter on the button and a VoiceOver
// activation both do, answers the question rather than reloading the page.
document.addEventListener("submit", (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.dataset["answerForm"] === undefined) return;
  event.preventDefault();
  run(submitAnswer());
});

searchInput.addEventListener("input", () => {
  window.clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => { run(runSearch()); }, 120);
});
searchDialog.addEventListener("keydown", (event) => {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Enter") return;
  const results = [...searchResults.querySelectorAll<HTMLButtonElement>(".search-result")];
  if (results.length === 0) return;
  const active = document.activeElement instanceof HTMLButtonElement ? results.indexOf(document.activeElement) : -1;
  if (event.key === "Enter") {
    if (active >= 0) {
      event.preventDefault();
      results[active]?.click();
    } else if (document.activeElement === searchInput) {
      event.preventDefault();
      results[0]?.click();
    }
    return;
  }
  event.preventDefault();
  const offset = event.key === "ArrowDown" ? 1 : -1;
  const next = active < 0 ? (offset > 0 ? 0 : results.length - 1) : (active + offset + results.length) % results.length;
  results[next]?.focus();
});

function setSearchMode(mode: typeof searchMode, search = true): void {
  searchMode = mode;
  document.querySelectorAll<HTMLElement>("[data-search-mode]").forEach((item) => {
    const selected = item.dataset.searchMode === mode;
    item.classList.toggle("active", selected);
    item.setAttribute("aria-selected", String(selected));
    item.tabIndex = selected ? 0 : -1;
  });
  const activeTab = select<HTMLElement>(`[data-search-mode='${mode}']`);
  searchResults.setAttribute("aria-labelledby", activeTab.id);
  select("[data-search-privacy]").textContent = mode === "exact" ? "Nothing leaves this device." : "This query is sent to RecallVault Cloud.";
  if (search) run(runSearch());
}

select<HTMLElement>(".search-modes").addEventListener("keydown", (event) => {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight" && event.key !== "Home" && event.key !== "End") return;
  event.preventDefault();
  const mode = event.key === "ArrowLeft" || event.key === "Home" ? "exact" : "semantic";
  setSearchMode(mode);
  select<HTMLButtonElement>(`[data-search-mode='${mode}']`).focus();
});

/**
 * The tree is one tab stop with arrow-key movement inside it, which is what
 * keeps 857 notes from being 857 stops between the search box and the reader.
 */
noteList.addEventListener("keydown", (event) => {
  const active = document.activeElement instanceof HTMLElement ? document.activeElement.dataset["row"] : undefined;
  const rows = treeRows(currentTree(), expandedFolders);
  const intent = treeKeyIntent(event.key, rows, rows.findIndex((row) => rowKey(row) === active));
  if (!intent) return;
  event.preventDefault();
  switch (intent.kind) {
    case "focus": focusTreeRow(intent.key); break;
    case "expand": toggleFolder(intent.path, true); break;
    case "collapse": toggleFolder(intent.path, false); break;
    case "activate":
      if (intent.row.kind === "folder") toggleFolder(intent.row.path);
      else run(selectNote(intent.row.note.id));
      break;
  }
});

document.addEventListener("keydown", (event) => {
  const mod = event.metaKey || event.ctrlKey;
  const typing = event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement;
  if (event.key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey && !typing) {
    event.preventDefault();
    navigate({ kind: "search", query: "", mode: "exact" });
    searchInput.focus();
  } else if (mod && event.key.toLocaleLowerCase() === "k") {
    event.preventDefault();
    navigate({ kind: "search", query: "", mode: "exact" });
    searchInput.focus();
  } else if (!mod && event.key.toLocaleLowerCase() === "a" && !typing) {
    event.preventDefault(); openAsk("create", "");
  } else if (!mod && event.key === "[" && !typing) {
    event.preventDefault(); setRails({ ...rails, navCollapsed: !rails.navCollapsed });
  } else if (!mod && event.key === "]" && !typing) {
    event.preventDefault(); setRails({ ...rails, contextCollapsed: !rails.contextCollapsed });
  }
});

// Regaining the network is the moment to catch up, and to drop the offline
// notice the moment the hosted vault actually answers.
window.addEventListener("online", () => {
  if (accountId) run(syncNow());
});

// The address bar drives every surface, so back/forward and a pasted link take
// the same path as a click.
window.addEventListener("hashchange", () => { applyDestination(); });

// Coming back to the tab is the moment to replace guessed badges with counts
// the server stands behind, and to retry any decision still sitting unsent.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && accountId) run(reconcile());
});

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  deferredInstall = event;
  select<HTMLButtonElement>("[data-action='install']").hidden = false;
});
/**
 * A new shell can arrive at any moment, and the worker claims control the
 * instant it activates. Nothing here reloads on its own: the offer is visible
 * and the reader decides, because a page that reloads itself mid-thought is a
 * worse failure than a page that is one version behind.
 */
function offerUpdate(signal: UpdateSignal): void {
  const prompt = updatePrompt(signal, {
    queuedNotes: notes.filter((note) => note.state === "queued" || note.state === "conflict").length,
    unsavedEditor: askDialog.open && askPromptField.value.trim() !== "",
  });
  if (readDismissedRelease(browserStore()) === RELEASE && signal === "ready") return;
  select("[data-update-message]").textContent = prompt.message;
  select<HTMLButtonElement>("[data-action='reload-update']").hidden = !prompt.reloadable;
  updateBar.hidden = false;
}

function dismissUpdate(): void {
  updateBar.hidden = true;
  writeDismissedRelease(browserStore(), RELEASE);
}

/**
 * The escape hatch for a shell so stale that no update reaches it. Everything
 * it removes is a cache; the vault lives in IndexedDB and OPFS and survives.
 */
async function discardCachedShell(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      await discardShell({
        registrations: () => navigator.serviceWorker.getRegistrations(),
        keys: () => caches.keys(),
        remove: (key) => caches.delete(key),
      });
    }
  } catch {
    // A browser that refuses to enumerate caches still deserves the reload,
    // which on its own re-fetches the shell from the network.
  }
  location.reload();
}

/**
 * The browser side of Web Push. The worker turns a payload-free push into a
 * notification by reading the live inbox (`vite.config.ts`); this is only the
 * subscription, which the server keeps as one of the tenant's doorbells.
 */
function webPushDeps(snapshot = publication.capture()): WebPushDeps {
  const api = hostedApi(snapshot);
  return {
    pushManager: async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return undefined;
      const registration = await navigator.serviceWorker.getRegistration();
      return registration?.pushManager;
    },
    permission: () => Notification.permission,
    requestPermission: () => Notification.requestPermission(),
    publicKey: () => fetchPushPublicKey(api),
    register: (subscription) => registerWebPush(api, subscription, browserDeviceId()),
  };
}

/** One stable id per browser profile, so re-registering updates one row. */
function browserDeviceId(): string {
  const store = browserStore();
  const existing = store?.getItem("recallvault.device");
  if (existing) return existing;
  const minted = `web-${crypto.randomUUID()}`;
  store?.setItem("recallvault.device", minted);
  return minted;
}

function showPushStatus(outcome: PushOutcome | undefined): void {
  const permission = "Notification" in window ? Notification.permission : undefined;
  select("[data-push-status]").textContent = pushStatusLabel(outcome, permission);
  select<HTMLButtonElement>("[data-action='enable-push']").hidden = outcome?.kind === "enabled" || outcome?.kind === "unsupported";
}

/** Only ever from the button: the permission prompt has to follow a click. */
async function turnOnNotifications(): Promise<void> {
  if (!accountId.startsWith("hosted:")) { showToast("Sign in to the hosted vault first."); return; }
  const snapshot = publication.capture();
  const outcome = await enableWebPush(webPushDeps(snapshot));
  publication.publish(snapshot, () => { showPushStatus(outcome); trace(outcome.kind === "enabled" ? "notifications turned on" : `notifications: ${outcome.kind}`); });
}

// The worker cannot change the page's address itself, so a notification
// click hands the destination over and the page walks the normal route.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event: MessageEvent<{ kind?: string; href?: string }>) => {
    if (event.data?.kind === "navigate" && typeof event.data.href === "string" && event.data.href.startsWith("#/")) {
      location.hash = event.data.href;
    }
  });
}

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  const registerOfflineShell = () => {
    void navigator.serviceWorker.register("/sw.js").then((registration) => {
      watchForUpdate(navigator.serviceWorker, registration, offerUpdate);
    }).catch(() => { /* No worker means no offline shell, and no update to offer. */ });
  };
  // The title core awaits WASM during module evaluation. The document may
  // have finished loading before this module reaches its registration code.
  if (document.readyState === "complete") registerOfflineShell();
  else window.addEventListener("load", registerOfflineShell, { once: true });
}

async function openHostedSession(session: BrowserSession, validated = true): Promise<void> {
  endWebSession().close();
  await composer.reset();
  accountId = `hosted:${session.accountId}`;
  const snapshot = publication.activate(accountId);
  if (validated) broadcastSession(accountId);
  if (validated) rememberValidatedSession(browserStore(), session);
  selectedId = browserStore()?.getItem(`recallvault.selected:${accountId}`) ?? "";
  expandedFolders = readExpandedFolders(browserStore(), accountId);
  intents = readIntents(browserStore(), accountId);
  client.close();
  client = new VaultWorkerClient();
  offlineStart = !validated;
  authStatus.textContent = "Opening your cached vault…";
  await initialize();
  if (!publication.current(snapshot)) return;
  // A link the user followed outranks the note they last had open.
  if (location.hash) applyDestination();
  else if (selectedId) navigate({ kind: "note", id: selectedId });
  run(reconcile());
  showPushStatus(undefined);
  run(refreshWebPush(webPushDeps(snapshot)).then((outcome) => publication.publish(snapshot, () => { if (outcome) showPushStatus(outcome); })));
  authGate.hidden = true;
  appFrame.hidden = false;
  // An empty store under a known account is a cache that went missing, not an
  // empty vault: restore it before the shell can be mistaken for data loss.
  await restoreOnStartup({ noteCount: notes.length, hosted: true, validated }, {
    sync: syncNow,
    show: (phase) => { publication.publish(snapshot, () => { startupState = phase; render(); }); },
  });
  if (!publication.current(snapshot)) return;
  const first = session.email?.trim().charAt(0).toLocaleUpperCase();
  if (first) select(".avatar").textContent = first;
}

function loadProviderScript(src: string): Promise<void> {
  const existing = document.querySelector<HTMLScriptElement>(`script[src='${src}']`);
  if (existing?.dataset.loaded === "true") return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = existing ?? document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => { script.dataset.loaded = "true"; resolve(); }, { once: true });
    script.addEventListener("error", () => reject(new Error("The identity provider could not load.")), { once: true });
    if (!existing) document.head.append(script);
  });
}

async function sha256Base64Url(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  let binary = "";
  for (const byte of new Uint8Array(digest)) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

async function finishProviderSignIn(provider: "apple" | "google", idToken: string, challenge: string, purpose: "session" | "link" = "session"): Promise<void> {
  if (purpose === "link") {
    await linkProviderToken(API_ORIGIN, provider, idToken, challenge);
    showToast(`${provider === "apple" ? "Apple" : "Google"} sign-in connected`);
    return;
  }
  if (isMcpAuthorization) {
    authStatus.textContent = "Preparing the MCP permission screen…";
    const authorization = await exchangeProviderAuthorization(API_ORIGIN, provider, idToken, challenge);
    location.assign(authorization.continueTo);
    return;
  }
  authStatus.textContent = "Securing your RecallVault session…";
  const session = await exchangeProviderToken(API_ORIGIN, provider, idToken, challenge);
  await openHostedSession(session);
  showToast(`Signed in with ${provider === "apple" ? "Apple" : "Google"}`);
}

async function signInWithApple(purpose: "session" | "link" = "session"): Promise<void> {
  if (!appleClientId) throw new Error("Sign in with Apple is not configured yet.");
  authStatus.textContent = "Opening Apple sign in…";
  const [{ challenge, nonce }] = await Promise.all([
    requestProviderChallenge(API_ORIGIN, purpose),
    loadProviderScript("https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"),
  ]);
  if (!window.AppleID) throw new Error("Apple sign in did not become available.");
  window.AppleID.auth.init({
    clientId: appleClientId,
    scope: "name email",
    redirectURI: location.origin,
    usePopup: true,
    nonce: await sha256Base64Url(nonce),
  });
  applePendingChallenge = challenge;
  applePendingPurpose = purpose;
  await window.AppleID.auth.signIn();
}

async function initializeAppleButton(): Promise<void> {
  if (!appleClientId) throw new Error("Sign in with Apple is not configured yet.");
  const [{ challenge, nonce }] = await Promise.all([
    isMcpAuthorization
      ? Promise.resolve({ challenge: mcpAuthorization!, nonce: mcpAuthorizationNonce!, expiresIn: 600 })
      : requestProviderChallenge(API_ORIGIN),
    loadProviderScript("https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"),
  ]);
  if (!window.AppleID) throw new Error("Apple sign in did not become available.");
  applePendingChallenge = challenge;
  applePendingPurpose = "session";
  window.AppleID.auth.init({
    clientId: appleClientId,
    scope: "name email",
    redirectURI: location.origin,
    usePopup: true,
    nonce: await sha256Base64Url(nonce),
  });
}

async function initializeGoogleButton(purpose: "session" | "link" = "session"): Promise<void> {
  if (!googleClientId) throw new Error("Sign in with Google is not configured yet.");
  const [{ challenge, nonce }] = await Promise.all([
    isMcpAuthorization && purpose === "session"
      ? Promise.resolve({ challenge: mcpAuthorization!, nonce: mcpAuthorizationNonce!, expiresIn: 600 })
      : requestProviderChallenge(API_ORIGIN, purpose),
    loadProviderScript("https://accounts.google.com/gsi/client"),
  ]);
  if (!window.google) throw new Error("Google sign in did not become available.");
  const host = select<HTMLElement>(purpose === "link" ? "[data-google-link]" : "[data-google-sign-in]");
  host.replaceChildren();
  window.google.accounts.id.initialize({
    client_id: googleClientId,
    nonce,
    callback: (response) => {
      if (!response.credential) {
        authStatus.textContent = "Google did not return an identity token.";
        return;
      }
      void finishProviderSignIn("google", response.credential, challenge, purpose).catch(purpose === "link" ? showLinkError : showAuthError);
    },
  });
  window.google.accounts.id.renderButton(host, {
    type: "standard", theme: "outline", size: "large", text: "signin_with", shape: "rectangular", width: 360,
  });
}

function showLinkError(error: unknown): void {
  showToast(error instanceof Error ? error.message : "Identity linking could not finish.");
}

function showAuthError(error: unknown): void {
  if (error instanceof AuthError && error.code === "not_invited") {
    showTerminalAuthFailure(`${error.message} Signing in again will not change that — ask the operator for an invite, then reload.`);
    return;
  }
  authStatus.textContent = error instanceof Error ? error.message : "Sign in could not finish.";
  select<HTMLButtonElement>("[data-action='retry-auth']").hidden = false;
}

async function initializeProviderButtons(): Promise<void> {
  select<HTMLElement>("[data-provider-buttons]").hidden = false;
  select<HTMLButtonElement>("[data-action='retry-auth']").hidden = true;
  authStatus.textContent = isMcpAuthorization
    ? "Choose Apple or Google to authorize your MCP client."
    : "Choose Apple or Google to continue.";
  const unavailable: string[] = [];
  if (appleClientId) await initializeAppleButton().catch(showAuthError);
  else unavailable.push("Apple");
  if (googleClientId) {
    await initializeGoogleButton().catch(showAuthError);
  } else {
    unavailable.push("Google");
  }
  if (unavailable.length === 2) authStatus.textContent = "Sign-in provider IDs still need to be configured for this deployment.";
}

async function loadRuntimeAuthConfig(): Promise<void> {
  await withDeadline(async (signal) => {
    const response = await fetch("/config.json", { cache: "no-store", signal });
    if (!response.ok || !(response.headers.get("content-type") ?? "").includes("application/json")) return;
    const config = await response.json() as { readonly appleClientId?: string; readonly googleClientId?: string };
    appleClientId = config.appleClientId || appleClientId;
    googleClientId = config.googleClientId || googleClientId;
  }, 15_000, "Account configuration took too long to load. Try again.");
}

document.addEventListener("AppleIDSignInOnSuccess", (event) => {
  const idToken = (event as CustomEvent<{ readonly authorization?: { readonly id_token?: string } }>).detail.authorization?.id_token;
  const challenge = applePendingChallenge;
  const purpose = applePendingPurpose;
  if (!idToken || !challenge) { showAuthError(new Error("Apple did not return a complete sign-in response.")); return; }
  applePendingChallenge = undefined;
  void finishProviderSignIn("apple", idToken, challenge, purpose).catch(purpose === "link" ? showLinkError : showAuthError);
});
document.addEventListener("AppleIDSignInOnFailure", (event) => {
  applePendingChallenge = undefined;
  const detail = (event as CustomEvent<{ readonly error?: string }>).detail;
  showAuthError(new Error(detail?.error || "Apple sign in was cancelled."));
});
select<HTMLButtonElement>("[data-action='retry-auth']").addEventListener("click", () => {
  location.reload();
});

void (async () => {
  try {
    await loadRuntimeAuthConfig();
    if (isMcpAuthorization) {
      authGate.hidden = false;
      appFrame.hidden = true;
      await initializeProviderButtons();
      return;
    }
    const session = await readBrowserSession(API_ORIGIN);
    if (session) {
      await openHostedSession(session);
    } else {
      authGate.hidden = false;
      appFrame.hidden = true;
      await initializeProviderButtons();
      return;
    }
  } catch (error) {
    if (client.failure) { reportFailure(error); return; }
    // A refusal is an answer, not an outage: only an unreachable server may
    // fall back to the cached vault.
    const cachedAccount = error instanceof AuthError ? undefined : cachedOfflineAccount(browserStore());
    if (cachedAccount) {
      await openHostedSession({ accountId: cachedAccount }, false);
      showToast("Offline — your cached vault is open. Changes will sync later.");
    } else {
      authGate.hidden = false;
      appFrame.hidden = true;
      showAuthError(error);
      return;
    }
  }
  const url = new URL(location.href);
  if (url.searchParams.has("ask") || url.searchParams.has("capture")) openAsk("create", "");
  if (url.searchParams.has("search")) {
    navigate({ kind: "search", query: "", mode: "exact" });
    searchInput.focus();
  }
  if (url.search) history.replaceState(null, "", url.pathname);
})().catch((error: unknown) => {
  showFatalState(root, {
    headline: "RecallVault could not open.",
    detail: error instanceof Error ? error.message : "Unknown startup failure",
  }, () => location.reload());
});
